import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  Output,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { ReplaySubject, Subscription } from 'rxjs';

@Component({
    selector: 'app-select-input-search',
    templateUrl: './select-input-search.component.html',
    styleUrls: ['./select-input-search.component.scss'],
    imports: [
        CommonModule,
        MatFormFieldModule,
        NgxMatSelectSearchModule,
        MatSelectModule,
        FormsModule,
        ReactiveFormsModule,
        TranslateModule,
    ],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: forwardRef(() => SelectInputSearchComponent),
        },
    ]
})
export class SelectInputSearchComponent
  implements OnDestroy, OnChanges, ControlValueAccessor
{
  /** Nombre que aparecerá en el input */
  @Input() label: string;
  /** Id interno que maneja el input */
  @Input() id: string;
  /** Texto placeholder para el input */
  @Input() placeholder: string;
  /** Opciones que mostrará el input */
  @Input() options: any[] = [];
  /** Nombre del parámetro que contiene el valor de la opción */
  @Input() valueName = 'value';
  /** Nombre del parámetro que contiene el identificador de la opción */
  @Input() idName = 'id';
  /** Texto que aparece en la primera opción para quitar la selección */
  @Input() selectNoneText = 'ninguno';
  /** Texto que aparece en la primera opción para quitar la selección */
  @Input() selectNoneValue = 'null';
  /** Activar el seleccionar todos */
  @Input() selectAllDesactive = false;
  /** Deshabilitar el input */
  @Input() disabled;
  /** Si la selección es múltiple */
  @Input() multiple = false;
  /** Función para modificar como se mostrará el texto del select */
  @Input() displayValue: Function;
  /** Emite el evento de cambio en la selección */
  @Output() selectionChange = new EventEmitter();

  @Input() value: any;

  public selectedValueFilter: FormControl<string> = new FormControl();
  public filteredValues: ReplaySubject<any[]> = new ReplaySubject<any[]>();
  public filterSub: Subscription;

  constructor() {}

  ngOnChanges(): void {
    if(this.options) this.filteredValues.next(this.options.slice());

    this.filterSub = this.selectedValueFilter.valueChanges.subscribe(() => {
      this.filterValues();
    });
  }

  emmitSelectionCHange(value) {
    this.selectionChange.emit(value);
  }

  /**
   * Filtra una lista de parámetros basada en una palabra clave de búsqueda y una lista de
   * parámetros seleccionados.
   * @returns nada si `this.data.parameters` es falso. De lo contrario, está devolviendo los parámetros
   * filtrados.
   */
  protected filterValues() {
    if (!this.options) {
      return;
    }
    let search = this.selectedValueFilter.value;
    if (!search) {
      this.filteredValues.next(this.options.slice());
      return;
    } else {
      search = search.toLowerCase();
    }
    this.filteredValues.next(
      this.options.filter(
        parameter =>
          this.valueName 
            ? parameter[this.valueName].toLowerCase().indexOf(search) > -1
            : parameter.toLowerCase().indexOf(search) > -1
      )
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onChange = (_: any) => {};

  onTouch = () => {};

  onInput(value: string) {
    this.value = value;
    this.emmitSelectionCHange(this.value);
    this.onTouch();
    this.onChange(this.value);
  }
  writeValue(value: any): void {
    if (value) {
      this.value = value || '';
    } else {
      this.value = '';
    }
  }
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  ngOnDestroy(): void {
    this.filterSub?.unsubscribe();
  }
}
